/** @format */

import { useCallback, useEffect, useRef, useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api";
import { TLocation } from "../../../redux-modules/declarations/maps/locations";
import { TShiftType } from "../../../redux-modules/declarations/maps/shiftTypes";
import { TTurniSettimana } from "../../../redux-modules/declarations/maps/turniSettimana";
import apiClient from "../../../utils/apiClient";
import { showNotification } from "../../../utils/showNotification";

export interface TNuovoTurno {
  user_id: number;
  shift_type_id: string;
  date: string;
  office_id?: number;
  hidden: boolean;
  /** nota facoltativa del turno */
  notes?: string;
}

interface TAvvisoSalvataggio {
  messaggio: string;
}

interface TErroreApi {
  response?: { data?: { message?: string; errors?: Record<string, string[]> } };
}

export const messaggioErrore = (errore: unknown, ripiego: string): string => {
  const dati = (errore as TErroreApi)?.response?.data;
  if (dati?.errors) return Object.values(dati.errors).join("\n");
  return dati?.message || ripiego;
};

export interface TUseTurniSettimana {
  dati: TTurniSettimana | null;
  caricamento: boolean;
  aggiornamento: boolean;
  errore: string | null;
  tipiTurno: TShiftType[];
  sedi: TLocation[];
  ricarica: () => void;
  assegna: (turno: TNuovoTurno) => Promise<boolean>;
  elimina: (turnoId: number) => Promise<boolean>;
  cambiaOrario: (turnoId: number, tipoTurnoId: string, sedeId: number | null) => Promise<boolean>;
  accettaCambio: (turnoId: number, tipoTurnoId: string) => Promise<boolean>;
  rifiutaCambio: (turnoId: number) => Promise<boolean>;
  salvaNota: (turnoId: number, nota: string) => Promise<boolean>;
  eliminaNota: (turnoId: number) => Promise<boolean>;
  replica: (
    utenti: string[],
    origineDal: string,
    origineAl: string,
    destinazioneDal: string,
    destinazioneAl: string,
    gruppo?: string,
    reparto?: string,
  ) => void;
}

/**
 * Dati della vista Turni settimanale: una sola lettura per settimana (GET /turni/settimana).
 *
 * Dopo ogni modifica la settimana si rilegge: stati, avvisi e totali li calcola il backend,
 * e una lettura costa un numero fisso di query. Durante la rilettura i dati restano a video.
 */
export const useTurniSettimana = (lunedi: string): TUseTurniSettimana => {
  const [dati, setDati] = useState<TTurniSettimana | null>(null);
  const [caricamento, setCaricamento] = useState(true);
  const [aggiornamento, setAggiornamento] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [tipiTurno, setTipiTurno] = useState<TShiftType[]>([]);
  const [sedi, setSedi] = useState<TLocation[]>([]);
  const richiesta = useRef(0);
  const settimanaCaricata = useRef<string | null>(null);

  const leggi = useCallback((): void => {
    const numero = ++richiesta.current;
    const stessaSettimana = settimanaCaricata.current === lunedi;
    if (stessaSettimana) setAggiornamento(true);
    else setCaricamento(true);

    apiClient
      .get(API_ENDPOINTS.SHIFTS_WEEK, { params: { data: lunedi } })
      .then(res => {
        // una risposta arrivata dopo un cambio di settimana non sovrascrive quella nuova
        if (numero !== richiesta.current) return;
        setDati(res.data as TTurniSettimana);
        settimanaCaricata.current = lunedi;
        setErrore(null);
      })
      .catch(err => {
        if (numero !== richiesta.current) return;
        setErrore(messaggioErrore(err, "Impossibile caricare i turni della settimana"));
      })
      .finally(() => {
        if (numero !== richiesta.current) return;
        setCaricamento(false);
        setAggiornamento(false);
      });
  }, [lunedi]);

  useEffect(() => {
    leggi();
  }, [leggi]);

  useEffect(() => {
    apiClient
      .get(API_ENDPOINTS.SHIFT_TYPES)
      .then(res => setTipiTurno((res.data?.data || []) as TShiftType[]))
      .catch(() => setTipiTurno([]));
    apiClient
      .get(API_ENDPOINTS.LOCATIONS, { params: { page_size: 100 } })
      .then(res => setSedi((res.data?.data || []) as TLocation[]))
      .catch(() => setSedi([]));
  }, []);

  const assegna = useCallback(
    (turno: TNuovoTurno): Promise<boolean> =>
      apiClient
        .post(API_ENDPOINTS.SHIFTS, turno)
        .then(res => {
          const avvisi = (res.data?.avvisi || []) as TAvvisoSalvataggio[];
          if (avvisi.length) {
            showNotification(
              "warning",
              "Turno assegnato, con avvisi del contratto",
              avvisi.map(a => a.messaggio).join("\n"),
              6,
            );
          } else {
            showNotification("success", "Turno assegnato", "");
          }
          leggi();
          return true;
        })
        .catch(err => {
          showNotification(
            "error",
            "Turno non assegnato",
            messaggioErrore(err, "Errore durante il salvataggio"),
          );
          return false;
        }),
    [leggi],
  );

  const elimina = useCallback(
    (turnoId: number): Promise<boolean> =>
      apiClient
        .delete(`${API_ENDPOINTS.SHIFTS}/${turnoId}`)
        .then(() => {
          showNotification("success", "Turno eliminato", "");
          leggi();
          return true;
        })
        .catch(err => {
          showNotification(
            "error",
            "Turno non eliminato",
            messaggioErrore(err, "Errore durante l'eliminazione"),
          );
          return false;
        }),
    [leggi],
  );

  const cambiaOrario = useCallback(
    (turnoId: number, tipoTurnoId: string, sedeId: number | null): Promise<boolean> =>
      apiClient
        .put(`${API_ENDPOINTS.SHIFTS}/${turnoId}`, {
          shift_type_id: tipoTurnoId,
          office_id: sedeId,
        })
        .then(() => {
          showNotification("success", "Orario cambiato", "");
          leggi();
          return true;
        })
        .catch(err => {
          showNotification(
            "error",
            "Orario non cambiato",
            messaggioErrore(err, "Errore durante la modifica"),
          );
          return false;
        }),
    [leggi],
  );

  const accettaCambio = useCallback(
    (turnoId: number, tipoTurnoId: string): Promise<boolean> =>
      apiClient
        .post(API_ENDPOINTS.SHIFTS_SWITCH_MANAGE, {
          shift_planning_id: turnoId,
          shift_type_id: tipoTurnoId,
        })
        .then(() => {
          showNotification("success", "Cambio turno effettuato", "");
          leggi();
          return true;
        })
        .catch(err => {
          showNotification(
            "error",
            "Cambio turno non effettuato",
            messaggioErrore(err, "Errore durante il cambio turno"),
          );
          return false;
        }),
    [leggi],
  );

  const rifiutaCambio = useCallback(
    (turnoId: number): Promise<boolean> =>
      apiClient
        // stessi valori della vista precedente per annullare la richiesta
        .post(API_ENDPOINTS.SHIFTS_SWITCH, {
          shift_planning_id: turnoId,
          pending_switch_message: "",
          pending_switch: "00",
        })
        .then(() => {
          showNotification("success", "Richiesta di cambio rifiutata", "");
          leggi();
          return true;
        })
        .catch(err => {
          showNotification(
            "error",
            "Richiesta non aggiornata",
            messaggioErrore(err, "Errore durante l'aggiornamento"),
          );
          return false;
        }),
    [leggi],
  );

  // note del turno: stesse API della vista precedente (PUT/DELETE /shift_plannings/{id}/notes)
  const salvaNota = useCallback(
    (turnoId: number, nota: string): Promise<boolean> =>
      apiClient
        .put(`${API_ENDPOINTS.SHIFTS}/${turnoId}/notes`, { notes: nota.trim() })
        .then(() => {
          showNotification("success", "Nota salvata", "");
          leggi();
          return true;
        })
        .catch(err => {
          showNotification(
            "error",
            "Nota non salvata",
            messaggioErrore(err, "Errore durante il salvataggio della nota"),
          );
          return false;
        }),
    [leggi],
  );

  const eliminaNota = useCallback(
    (turnoId: number): Promise<boolean> =>
      apiClient
        .delete(`${API_ENDPOINTS.SHIFTS}/${turnoId}/notes`)
        .then(() => {
          showNotification("success", "Nota eliminata", "");
          leggi();
          return true;
        })
        .catch(err => {
          showNotification(
            "error",
            "Nota non eliminata",
            messaggioErrore(err, "Errore durante l'eliminazione della nota"),
          );
          return false;
        }),
    [leggi],
  );

  const replica = useCallback(
    (
      utenti: string[],
      origineDal: string,
      origineAl: string,
      destinazioneDal: string,
      destinazioneAl: string,
      gruppo?: string,
      reparto?: string,
    ): void => {
      apiClient
        .post(API_ENDPOINTS.SHIFTS + "/repeat", {
          user_ids: utenti,
          department_id: reparto,
          group_id: gruppo,
          start_date_from: origineDal,
          start_date_to: origineAl,
          end_date_from: destinazioneDal,
          end_date_to: destinazioneAl,
        })
        .then(() => {
          showNotification("success", "Turni replicati", "Turni replicati correttamente");
          leggi();
        })
        .catch(err => {
          showNotification(
            "error",
            "Turni non replicati",
            messaggioErrore(err, "Errore durante la replica"),
          );
        });
    },
    [leggi],
  );

  return {
    dati,
    caricamento,
    aggiornamento,
    errore,
    tipiTurno,
    sedi,
    ricarica: leggi,
    assegna,
    elimina,
    cambiaOrario,
    accettaCambio,
    rifiutaCambio,
    salvaNota,
    eliminaNota,
    replica,
  };
};
